import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import PreviewNoPHIBanner, { PREVIEW_MODE } from "../components/PreviewNoPHIBanner";
import FileViewer from "../components/FileViewer";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { CATEGORY_META, humanSize, ACCEPT_EXTENSIONS } from "../lib/files";
import { uploadManyWithProgress } from "../lib/upload";
import UploadProgressList from "../components/UploadProgressList";
import UploadExtras from "../components/UploadExtras";
import CBCTViewer from "../components/CBCTViewer";
import { renderMarkdown } from "../lib/markdown";
import { toast } from "sonner";
import {
  ArrowLeft, Edit, Upload, Loader2, Trash2, Eye, ImagePlus, FilePlus,
} from "lucide-react";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "imaging", label: "Imaging" },
  { id: "cases", label: "Case History" },
];

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [patient, setPatient] = useState(null);
  const [files, setFiles] = useState([]);
  const [cases, setCases] = useState([]);
  const [tab, setTab] = useState("profile");
  const [viewerFile, setViewerFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [cbctStudies, setCbctStudies] = useState([]);
  const [cbctOpen, setCbctOpen] = useState(null); // study object when modal open
  const [selectedCase, setSelectedCase] = useState(null);
  const fileInputRef = useRef(null);

  const loadCbct = async () => {
    try {
      const r = await api.get(`/patients/${id}/cbct-studies`);
      setCbctStudies(r.data.studies || []);
    } catch {
      /* non-blocking */
    }
  };

  const load = async () => {
    try {
      const [p, f, c] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patients/${id}/files`),
        api.get(`/consultations`, { params: { patient_id: id } }),
      ]);
      setPatient(p.data);
      setFiles(f.data);
      setCases(c.data);
      loadCbct();
    } catch (_) {
      toast.error("Failed to load patient");
    }
  };

  useEffect(() => {
    // Preview: patient records are hidden. Skip all fetches so no toast noise
    // and no needless 404 traffic; the render path below shows the banner.
    if (PREVIEW_MODE) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const items = Array.from(fileList).map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
      }));
      setUploadItems(
        items.map((it) => ({ id: it.id, name: it.name, pct: 0, status: "uploading" }))
      );
      let okCount = 0;
      await uploadManyWithProgress(`/patients/${id}/files`, items, {
        concurrency: 3,
        onItemProgress: (i, pct) =>
          setUploadItems((prev) =>
            prev.map((it) => (it.id === i ? { ...it, pct } : it))
          ),
        onItemSuccess: (i) => {
          okCount += 1;
          setUploadItems((prev) =>
            prev.map((it) =>
              it.id === i ? { ...it, pct: 100, status: "done" } : it
            )
          );
        },
        onItemError: (i, error) => {
          setUploadItems((prev) =>
            prev.map((it) =>
              it.id === i ? { ...it, status: "error", error } : it
            )
          );
          toast.error(error);
        },
      });
      if (okCount) toast.success(`Uploaded ${okCount}`);
      const r = await api.get(`/patients/${id}/files`);
      setFiles(r.data);
      loadCbct();
      setTimeout(() => setUploadItems([]), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete ${f.original_filename}?`)) return;
    await api.delete(`/files/${f.id}`);
    setFiles((prev) => prev.filter((x) => x.id !== f.id));
    toast.success("File deleted");
  };

  const startConsultWith = () => {
    nav(`/dashboard?patient_id=${id}`);
  };

  // Preview / no-PHI guardrail: never expose an individual real-patient
  // record. Show the banner and a "records disabled" panel instead of
  // rendering the profile / imaging / cases tabs.
  if (PREVIEW_MODE)
    return (
      <Layout>
        <Link
          to="/patients"
          className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition mb-4"
        >
          <ArrowLeft size={14} /> Back to Patients
        </Link>
        <div className="mb-6">
          <PreviewNoPHIBanner />
        </div>
        <div
          data-testid="patient-detail-preview-blocked"
          className="border border-dashed border-[var(--pa-border)] rounded-lg p-12 text-center text-sm text-[var(--pa-text-muted)]"
        >
          <div className="font-semibold text-[var(--pa-text)] mb-1">
            Patient records are disabled in Preview
          </div>
          <div>
            No individual patient record can be viewed, edited, or exported
            during beta testing. ARIA can still demonstrate the patient
            workflow from the Patients list.
          </div>
        </div>
      </Layout>
    );

  if (!patient)
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading patient…
        </div>
      </Layout>
    );

  const grouped = files.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <Layout>
      <Link
        to="/patients"
        className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
      >
        <ArrowLeft size={14} /> All patients
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-4">
        <div>
          <div className="kicker">Patient</div>
          <h1
            className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-1"
            data-testid="patient-name"
          >
            {patient.name}
          </h1>
          <div className="mt-2 text-xs text-[var(--pa-text-muted)]">
            {patient.dob && <span>DOB {patient.dob}</span>}
            {patient.sex && <span> · {patient.sex}</span>}
            {patient.asa_classification && (
              <span className="text-[var(--pa-gold)]"> · {patient.asa_classification}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={startConsultWith}
            data-testid="patient-new-consult"
            className="flex items-center gap-1.5 border border-[var(--pa-gold)] text-[var(--pa-gold)] text-sm px-4 py-2 rounded-md hover:bg-[var(--pa-gold)]/10 transition"
          >
            <FilePlus size={14} /> New consultation
          </button>
          <Link
            to={`/patients/${id}/edit`}
            data-testid="patient-edit"
            className="flex items-center gap-1.5 bg-[var(--pa-gold)] text-[var(--pa-bg)] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
          >
            <Edit size={14} /> Edit
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-[var(--pa-border)]">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2.5 text-sm transition-colors border-b-2 ${
              tab === t.id
                ? "border-[var(--pa-gold)] text-[var(--pa-gold)]"
                : "border-transparent text-[var(--pa-text-muted)] hover:text-[var(--pa-text)]"
            }`}
          >
            {t.label}
            {t.id === "imaging" && (
              <span className="ml-1.5 text-xs text-[var(--pa-text-dim)]">({files.length})</span>
            )}
            {t.id === "cases" && (
              <span className="ml-1.5 text-xs text-[var(--pa-text-dim)]">({cases.length})</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "profile" && <ProfileTab p={patient} />}

        {tab === "imaging" && (
          <ImagingTab
            patientId={id}
            files={files}
            grouped={grouped}
            uploading={uploading}
            uploadItems={uploadItems}
            cbctStudies={cbctStudies}
            onOpenCbct={(study) => setCbctOpen(study)}
            onUpload={upload}
            onDelete={deleteFile}
            onView={setViewerFile}
            fileInputRef={fileInputRef}
            onReloadFiles={async () => {
              const r = await api.get(`/patients/${id}/files`);
              setFiles(r.data);
              loadCbct();
            }}
          />
        )}

        {tab === "cases" && (
          <CasesTab cases={cases} selected={selectedCase} onSelect={setSelectedCase} />
        )}
      </div>

      {viewerFile && <FileViewer file={viewerFile} onClose={() => setViewerFile(null)} />}

      {cbctOpen && (
        <CBCTViewer
          study={cbctOpen}
          patientId={id}
          onClose={() => setCbctOpen(null)}
          onKeySlicesAdded={async () => {
            const r = await api.get(`/patients/${id}/files`);
            setFiles(r.data);
          }}
        />
      )}
    </Layout>
  );
}

function ProfileTab({ p }) {
  const Row = ({ label, value }) =>
    value ? (
      <div className="grid grid-cols-3 gap-4 py-3 border-b border-[var(--pa-border)]">
        <div className="text-xs uppercase tracking-wider text-[var(--pa-text-muted)] col-span-1">
          {label}
        </div>
        <div className="text-sm text-[var(--pa-text)] col-span-2 whitespace-pre-wrap">
          {value}
        </div>
      </div>
    ) : null;
  return (
    <div className="max-w-3xl bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6">
      <Row label="DOB" value={p.dob} />
      <Row label="Sex" value={p.sex} />
      <Row label="ASA" value={p.asa_classification} />
      <Row label="Blood pressure" value={p.blood_pressure} />
      <Row label="Smoking" value={p.smoking_status} />
      <Row label="Medical conditions" value={p.medical_conditions} />
      <Row label="Allergies" value={p.allergies} />
      <Row label="Medications" value={p.medications} />
      <Row label="Anticoagulants" value={p.anticoagulants} />
      <Row label="MRONJ risk" value={p.mronj_history} />
      <Row label="Notes" value={p.notes} />
    </div>
  );
}

function ImagingTab({ patientId, files, grouped, uploading, uploadItems, cbctStudies, onOpenCbct, onUpload, onDelete, onView, fileInputRef, onReloadFiles }) {
  const onDrop = (e) => {
    e.preventDefault();
    onUpload(e.dataTransfer.files);
  };
  return (
    <div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        data-testid="upload-dropzone"
        className="border border-dashed border-[var(--pa-border)] hover:border-[var(--pa-border-active)] rounded-lg p-8 text-center transition"
      >
        <ImagePlus size={28} className="text-[var(--pa-gold)] mx-auto" />
        <div className="font-serif text-lg text-[var(--pa-text)] mt-3">
          Drop imaging files here
        </div>
        <p className="mt-1.5 text-xs text-[var(--pa-text-muted)] max-w-md mx-auto">
          JPEG / PNG · DICOM (.dcm) including Galileos / Carestream CBCT exports · STL / PLY from
          Primescan and other intraoral scanners · exocad DentalCAD construction files
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_EXTENSIONS}
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => onUpload(e.target.files)}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="upload-button"
          className="mt-4 inline-flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload size={14} /> Select files
            </>
          )}
        </button>
      </div>

      <UploadProgressList items={uploadItems || []} dataTestId="patient-upload-progress" />

      <p className="text-[10px] text-[var(--pa-text-dim)] mt-2 leading-relaxed">
        <strong className="text-[var(--pa-text-muted)]">CBCT volumes:</strong> select{" "}
        <em>all</em> .dcm files from the Real Guide / Invivo / OnDemand export folder at
        once. ProsthoAI groups them by Study + Series UID into a single navigable study.
      </p>

      <UploadExtras
        patientId={patientId}
        onFiles={(picked) => onUpload(picked)}
        onFileImported={onReloadFiles}
      />

      {cbctStudies && cbctStudies.length > 0 && (
        <section
          data-testid="cbct-studies-section"
          className="mt-8 p-5 rounded-lg bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40"
        >
          <div className="kicker text-[var(--pa-gold)]">CBCT Volumes</div>
          <h3 className="font-serif text-xl text-[var(--pa-text)] mt-1">
            Navigable CBCT studies ({cbctStudies.length})
          </h3>
          <p className="text-[11px] text-[var(--pa-text-muted)] mt-1">
            DICOM series stitched together from Real Guide / Invivo / OnDemand exports.
            Click to open the slice viewer or push key slices to Claude AI.
          </p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {cbctStudies.map((s) => (
              <li
                key={s.series_uid}
                data-testid={`cbct-study-${s.series_uid.slice(-12)}`}
                className="p-4 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md hover:border-[var(--pa-gold)]/60 transition cursor-pointer group"
                onClick={() => onOpenCbct(s)}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--pa-gold)]">
                    {s.modality || "CT"} · {s.slice_count} slice{s.slice_count === 1 ? "" : "s"}
                  </div>
                  <div className="text-[10px] text-[var(--pa-text-dim)] group-hover:text-[var(--pa-gold)] transition">
                    Open viewer →
                  </div>
                </div>
                <div className="mt-1.5 text-sm text-[var(--pa-text)] truncate">
                  {s.series_description || s.study_description || "Untitled CBCT series"}
                </div>
                <div className="mt-1 text-[10px] text-[var(--pa-text-dim)] flex flex-wrap gap-x-2 gap-y-0.5">
                  {s.body_part_examined && <span>{s.body_part_examined}</span>}
                  {s.rows && s.columns && <span>· {s.columns}×{s.rows}</span>}
                  {s.slice_thickness_mm && (
                    <span>· {s.slice_thickness_mm.toFixed(2)} mm slice</span>
                  )}
                  {s.study_date && <span>· {s.study_date}</span>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-8 space-y-8">
        {files.length === 0 && (
          <div className="text-sm text-[var(--pa-text-muted)] text-center py-6">
            No files uploaded yet.
          </div>
        )}
        {Object.entries(grouped).map(([cat, list]) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.other;
          const Icon = meta.Icon;
          return (
            <div key={cat}>
              <div className="kicker mb-3 flex items-center gap-2" style={{ color: meta.color }}>
                <Icon size={14} /> {meta.label} ({list.length})
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((f) => (
                  <div
                    key={f.id}
                    data-testid={`file-card-${f.id}`}
                    className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-md p-4 flex items-center justify-between gap-3 hover:border-[var(--pa-border-active)] transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[var(--pa-text)] truncate">
                        {f.original_filename}
                      </div>
                      <div className="text-[10px] text-[var(--pa-text-dim)] mt-1">
                        {humanSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => onView(f)}
                        title="View"
                        data-testid={`file-view-${f.id}`}
                        className="text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)]"
                      >
                        <Eye size={15} />
                      </button>
                      <button
                        onClick={() => onDelete(f)}
                        title="Delete"
                        data-testid={`file-delete-${f.id}`}
                        className="text-[var(--pa-text-dim)] hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CasesTab({ cases, selected, onSelect }) {
  if (cases.length === 0) {
    return (
      <div className="text-sm text-[var(--pa-text-muted)] border border-dashed border-[var(--pa-border)] rounded-lg p-12 text-center">
        No consultations for this patient yet. Click <span className="text-[var(--pa-gold)]">New consultation</span> to start one.
      </div>
    );
  }
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <ul className="lg:col-span-5 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
        {cases.map((c) => (
          <li
            key={c.id}
            onClick={() => onSelect(c)}
            data-testid={`case-${c.id}`}
            className={`cursor-pointer border rounded-md p-4 transition ${
              selected?.id === c.id
                ? "border-[var(--pa-gold)] bg-[var(--pa-surface)]"
                : "border-[var(--pa-border)] bg-[var(--pa-surface)] hover:border-[var(--pa-border-active)]"
            }`}
          >
            <div className="font-serif text-base text-[var(--pa-text)]">{c.module_name}</div>
            <div className="text-xs text-[var(--pa-text-dim)] mt-1">
              {new Date(c.created_at).toLocaleString()}
            </div>
            {c.file_ids?.length > 0 && (
              <div className="kicker mt-1.5 text-[var(--pa-gold)]">
                {c.file_ids.length} image(s) attached
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="lg:col-span-7">
        {selected ? (
          <div className="bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg p-6">
            <div className="kicker">Consultation</div>
            <div className="font-serif text-xl text-[var(--pa-text)] mt-1">
              {selected.module_name}
            </div>
            <div className="gold-divider my-4" />
            <AiOutputDisclaimer className="mb-4" />
            <div
              className="prose-clinical"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.response) }}
            />
          </div>
        ) : (
          <div className="bg-[var(--pa-surface)] border border-dashed border-[var(--pa-border)] rounded-lg p-8 text-center text-sm text-[var(--pa-text-muted)]">
            Select a consultation to view
          </div>
        )}
      </div>
    </div>
  );
}
